import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User as UserIcon } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/football", label: "Football" },
  { to: "/cheer", label: "Cheer" },
  { to: "/7v7", label: "7v7" },
  { to: "/pre-registration", label: "Pre-Registration" },
  { to: "/registration", label: "Registration" },
  { to: "/schedule", label: "Schedule" },
  { to: "/news", label: "News" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${
      isActive ? "text-scu-electric" : "text-white/80 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-white/10 bg-scu-obsidian/85 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="SCU Utes" className="h-10 w-auto" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-bebas text-lg text-white tracking-wider">ST. CHARLES UNITED</span>
            <span className="hidden xl:inline text-[10px] uppercase tracking-[0.3em] text-scu-electric">Est. 2026 · Inaugural 2027</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} end={l.to === "/"} data-testid={`nav-link-${l.label.toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="nav-user-btn" variant="ghost" className="text-white hover:bg-white/10 hover:text-white gap-2">
                  <UserIcon className="h-4 w-4" />
                  <span className="hidden md:inline">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs uppercase tracking-widest">{user.role === "admin" ? "Administrator" : "Parent"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" ? (
                  <DropdownMenuItem data-testid="nav-admin-dashboard" onClick={() => nav("/admin")}>Admin Dashboard</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid="nav-parent-dashboard" onClick={() => nav("/portal")}>Parent Portal</DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="nav-logout" onClick={async () => { await logout(); nav("/"); }}>
                  <LogOut className="mr-2 h-4 w-4" /> Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                data-testid="nav-admin-portal-btn"
                onClick={() => nav("/login?admin=1")}
                variant="outline"
                className="inline-flex border-white/25 bg-transparent text-white hover:bg-white hover:text-scu-obsidian font-bold uppercase tracking-widest text-[9px] md:text-xs px-2.5 md:px-5 h-8 md:h-9 rounded-md"
              >
                <span className="hidden sm:inline">Admin Portal</span>
                <span className="sm:hidden">Admin</span>
              </Button>
              <Button
                data-testid="nav-parent-portal-btn"
                onClick={() => nav("/login")}
                className="inline-flex bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest text-[9px] md:text-xs px-2.5 md:px-5 h-8 md:h-9 rounded-md"
              >
                <span className="hidden sm:inline">Parent Portal</span>
                <span className="sm:hidden">Parent</span>
              </Button>
            </>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-scu-obsidian border-l border-white/10 text-white w-[85vw] sm:w-[400px]">
              <div className="flex items-center justify-between mb-8">
                <img src={LOGO_URL} alt="SCU" className="h-10" />
                <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-white/70 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex flex-col gap-4">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => `font-bebas text-2xl tracking-wider ${isActive ? "text-scu-electric" : "text-white/85 hover:text-white"}`}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="mt-6 border-t border-white/10 pt-6 space-y-3">
                  {!user && (
                    <>
                      <Button data-testid="mobile-login" onClick={() => { setOpen(false); nav("/login"); }} className="w-full bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
                        Parent Portal Login
                      </Button>
                      <Button data-testid="mobile-admin-login" onClick={() => { setOpen(false); nav("/login?admin=1"); }} variant="outline" className="w-full border-white/25 bg-transparent text-white hover:bg-white hover:text-scu-obsidian font-bold uppercase tracking-widest">
                        Admin Portal Login
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
